import React, { memo, useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Tooltip, Spin } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { getApi, postApi } from "utils/apiCall";
import { getBase64URI } from "utils/MyMUtils";
import moment from "moment";
import {
  BRAND,
  INITIAL_COMPLIANCE,
  ComplianceStep,
  filterAllowedModules,
} from "./staffModalShared";

// ─── Document type map ────────────────────────────────────────────────────────

const DOCUMENT_TYPE_MAP = {
  flu:    "influenza",
  covid:  "covid",
  stat:   "statutory_doc",
  police: "police_check",
};

const formatDateForApi = (value) => {
  if (!value) return null;
  if (typeof value.format === "function") return value.format("YYYY-MM-DD");
  return null;
};

const buildDocumentMetadata = (key, document) => {
  switch (key) {
    case "flu":
    case "covid":
      return {
        vaccination_date: formatDateForApi(document.vaccinationDate),
        comment: "",
      };
    case "police":
      return {
        ref_number: document.referenceNumber.trim(),
        issue_date: formatDateForApi(document.issueDate),
        certificate_sited: Number(document.certificateSighted),
        are_there_offences: Number(document.offences),
      };
    case "stat":
    default:
      return {};
  }
};

// ─── Main modal ───────────────────────────────────────────────────────────────

const ComplianceUploadModal = ({ visible, onClose, staffId, staffName, onUploaded }) => {
  const [compliance, setCompliance] = useState(INITIAL_COMPLIANCE);
  const [saving, setSaving]         = useState(false);
  const [fetching, setFetching]     = useState(false);

  // Fetch existing compliance data and pre-fill when modal opens
  useEffect(() => {
    if (!visible || !staffId) return;
    let isMounted = true;
    setFetching(true);

    getApi(`/api/staff/listing/${staffId}`).then(({ error, result }) => {
      if (!isMounted) return;
      if (!error && result) {
        const data = result.result ?? result;
        const doc = data.document || {};

        const toMoment = (dateStr) => (dateStr ? moment(dateStr) : null);

        const isDocActive = (s) => s === "uploaded" || s === "expired" || s === "expiring_soon";

        setCompliance({
          flu: {
            file: isDocActive(doc.flu_vaccination?.status)
              ? { name: "flu_vaccination", isExisting: true } : null,
            vaccinationDate: toMoment(doc.flu_vaccination?.vaccination_date),
          },
          covid: {
            file: isDocActive(doc.covid_vaccination?.status)
              ? { name: "covid_vaccination", isExisting: true } : null,
            vaccinationDate: toMoment(doc.covid_vaccination?.vaccination_date),
          },
          stat: {
            file: isDocActive(doc.statutory_declaration?.status)
              ? { name: "statutory_declaration", isExisting: true } : null,
          },
          police: {
            file: isDocActive(doc.police_check?.status)
              ? { name: "police_check", isExisting: true } : null,
            referenceNumber: doc.police_check?.ref_number || "",
            certificateSighted: doc.police_check?.certificate_sighted != null
              ? doc.police_check.certificate_sighted === true || doc.police_check.certificate_sighted === "1" || doc.police_check.certificate_sighted === 1
              : null,
            offences: doc.police_check?.offences != null
              ? doc.police_check.offences === true || doc.police_check.offences === "1" || doc.police_check.offences === 1
              : null,
            issueDate: toMoment(doc.police_check?.issue_date),
          },
        });
      }
      setFetching(false);
    });

    return () => { isMounted = false; };
  }, [visible, staffId]);

  const handleFileChange = useCallback((key, file) => {
    setCompliance((prev) => ({ ...prev, [key]: { ...prev[key], file } }));
  }, []);

  const handleFieldChange = useCallback((itemKey, field, value) => {
    setCompliance((prev) => ({
      ...prev,
      [itemKey]: { ...prev[itemKey], [field]: value },
    }));
  }, []);

  const handleClose = useCallback(() => {
    setCompliance(INITIAL_COMPLIANCE);
    setSaving(false);
    setFetching(false);
    onClose();
  }, [onClose]);

  // Same validation as Add/Edit compliance step
  const hasValidCompliance = (() => {
    if (compliance.flu.file !== null && compliance.flu.vaccinationDate === null) return false;
    if (compliance.covid.file !== null && compliance.covid.vaccinationDate === null) return false;
    if (compliance.police.file !== null) {
      const d = compliance.police;
      if (!d.referenceNumber.trim() || d.certificateSighted === null || d.offences === null || d.issueDate === null)
        return false;
    }
    return true;
  })();

  const hasAnyFile = Object.values(compliance).some((d) => d.file !== null);

  const handleSave = useCallback(async () => {
    if (!staffId) return;
    setSaving(true);
    try {
      const documents = await Promise.all(
        Object.entries(compliance).map(async ([key, document]) => {
          if (!document.file || document.file.isExisting) return null;
          const { base64String } = await getBase64URI(document.file);
          return {
            document_type: DOCUMENT_TYPE_MAP[key],
            filename: document.file.name,
            file: base64String,
            metadata: buildDocumentMetadata(key, document),
          };
        })
      );

      const filteredDocs = documents.filter(Boolean);
      if (filteredDocs.length === 0) {
        toast.error("Please upload at least one document.");
        setSaving(false);
        return;
      }

      const { error, result } = await postApi("/api/staff/upload-documents", {
        user_id: staffId,
        documents: filteredDocs,
      });

      if (error || result?.success === false) {
        toast.error(result?.message || "Upload failed.");
        return;
      }

      toast.success(result?.message || "Documents uploaded successfully.");
      if (typeof onUploaded === "function") onUploaded();
      handleClose();
    } catch (err) {
      toast.error(err?.message || "Upload failed.");
    } finally {
      setSaving(false);
    }
  }, [compliance, staffId, handleClose, onUploaded]);

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      width={750}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#101828" }}>
            Upload Compliance Documents
          </div>
          {staffName && (
            <div style={{ fontSize: 12, color: "#5B6472", marginTop: 2 }}>
              {staffName}
            </div>
          )}
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Tooltip
            title={
              !hasAnyFile
                ? "Please upload at least one document."
                : !hasValidCompliance
                ? "Complete or remove uploaded documents to save."
                : ""
            }
          >
            <Button
              type="primary"
              style={{ background: BRAND, borderColor: BRAND }}
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasValidCompliance || !hasAnyFile}
            >
              Save Documents
            </Button>
          </Tooltip>
        </div>
      }
      bodyStyle={{ padding: "16px 20px" }}
      destroyOnClose
    >
      <Spin spinning={fetching}>
        <ComplianceStep
          compliance={compliance}
          onFileChange={handleFileChange}
          onFieldChange={handleFieldChange}
        />
      </Spin>
    </Modal>
  );
};

export default memo(ComplianceUploadModal);
